/**
 * Leave balances tab — entity-wide view of all employee leave balances.
 * Rendered when the `LeaveScreen` tab is `'balances'`.
 *
 * Uses `listEntityLeaveBalances` to fetch Total, Taken, Pending and
 * Available days for every employee × leave type, for the type's current
 * leave year. Total = remaining balance + taken + paid out; Available =
 * remaining balance − pending. The drawer's Adjust action moves the
 * remaining balance, so it shifts Total and Available together. The backend
 * applies the entity's leave policies (fixed annual / monthly accrual /
 * pro-rating / carry-forward) before answering, so a new employee's policy
 * entitlement appears here without anyone typing an opening balance.
 * Rows are sorted employee-first, then leave type for scannability.
 *
 * Recalculate affordance (write-capable grants): "Recalculate entitlements"
 * re-applies every policy now via `payroll.applyLeaveEntitlements` — useful
 * straight after editing a policy. It is idempotent and never overwrites a
 * balance (see the backend engine's doc comment).
 *
 * Adjust-balance affordance: each row has an "Adjust" action that opens the
 * adjust-balance drawer when the grant is write-capable.
 *
 * Record-opening-balance affordance (#24315): both the empty state AND the
 * non-empty view expose a top-level "Record opening balance" action that
 * opens a small employee + leave-type picker; on continue, the picker opens
 * the same `LeaveAdjustDrawer` used by per-row Adjust, with a synthetic
 * zero-target for the chosen pair. This unblocks a brand-new client where
 * no pay run has accrued any balance yet — without it, a pending leave
 * request could not be approved.
 *
 * The picker is only mounted while its drawer is open — a read-only grant
 * (which cannot open the picker) therefore never triggers `useLeaveTypes()`
 * inside it, and a write-capable grant only fires the leave-types fetch the
 * first time the admin actually clicks "Record opening balance".
 *
 * Table rows resolve human-readable display strings — the threaded
 * `employees` list is looked up by id, and balance-tracked leave-type codes
 * (annual/sick/casual) are resolved through the shared `leaveTypeLabelKey`
 * helper. Both fall back to the raw id/code when a lookup misses, so a
 * mid-fetch or unrecognised custom type still renders something rather than
 * a blank cell.
 */
import { useState } from 'react';
import { useTranslation } from '@mukil/i18n';
import {
  Button,
  EmptyState,
  GlassTable,
  SkeletonList,
} from '@jumpstone/ui-react';
import { useEntityLeaveBalances } from '../../data/use_entity_leave_balances';
import { LeaveAdjustDrawer } from './leave_adjust_drawer';
import type {
  LeaveAdjustResult,
  LeaveAdjustTarget,
} from './leave_adjust_drawer';
import {
  LeaveOpeningBalancePicker,
  type EmployeesStatus,
} from './leave_opening_balance_picker';
import {
  buildOpeningBalanceTarget,
  employeeNameById,
} from './leave_opening_balance_utils';
import { useAuth } from '../../lib/auth/auth_context';
import { isWriteCapableGrant } from '../../data/use_pay_runs';
import { useOAuthEntityContext } from '../../lib/auth/oauth_entity_context';
import { getTrpcClient } from '../../lib/trpc';
import { format_leave_days } from '../../lib/format';
import type { LeaveEmployeeLookup } from '../../data/wagepro_store_leave';
import { leaveTypeLabelKey } from '../../data/wagepro_store_leave_balances';

export interface LeaveBalancesTabProps {
  /**
   * Employees available to the parent `LeaveScreen` — threaded in from
   * `useLeaveRequests().employees` so the tab does not fire a second
   * `listEmployees` fetch. Used by the "Record opening balance" picker to
   * populate its employee select, and by the table to resolve each row's
   * employee display name.
   */
  employees: readonly LeaveEmployeeLookup[];
  /**
   * Load status of the threaded `employees` list. `[]` alone cannot
   * distinguish "still loading", "failed to load", and "loaded, no employees
   * configured yet" — the picker's warning/error branches key off this
   * status so a mid-fetch or errored state does not render a misleading
   * "add an employee" alert.
   */
  employees_status: EmployeesStatus;
  /**
   * `useLeaveRequests().mutation_version` from the parent screen. Bumped on
   * every successful record / approve / reject / cancel so the Pending and
   * Available columns re-fetch — e.g. a leave recorded via the header's
   * "Record leave" action while this tab is open.
   */
  refresh_key?: number;
}

export function LeaveBalancesTab({
  employees,
  employees_status,
  refresh_key = 0,
}: LeaveBalancesTabProps): React.JSX.Element {
  const { t, locale } = useTranslation('wp');
  const { state, balances, is_loading, retry, apply_entitlements } =
    useEntityLeaveBalances(refresh_key);
  const { grant } = useAuth();
  const entityContext = useOAuthEntityContext();

  const [adjust_target, set_adjust_target] = useState<LeaveAdjustTarget | null>(
    null,
  );
  const [opening_picker_open, set_opening_picker_open] = useState(false);
  const [is_recalculating, set_is_recalculating] = useState(false);
  const [recalc_message, set_recalc_message] = useState<{
    tone: 'ok' | 'error';
    text: string;
  } | null>(null);

  const can_write = isWriteCapableGrant(grant);

  const readyEntity = entityContext.status === 'ready' ? entityContext : null;

  function format_days(value: number): string {
    return format_leave_days(value, locale);
  }

  function label_for_leave_type(code: string): string {
    const key = leaveTypeLabelKey(code);
    return key ? t(key) : code;
  }

  const adjust_fn:
    | ((input: {
        employee_id: string;
        leave_type: string;
        delta_days_scaled: number;
        reason: string;
      }) => Promise<LeaveAdjustResult>)
    | undefined =
    can_write && readyEntity !== null
      ? async (input) => {
          try {
            const client = getTrpcClient();
            await client.payroll.adjustLeaveBalance.mutate({
              firm_id: readyEntity.firm_id,
              entity_id: readyEntity.entity_id,
              ...input,
            });
            retry();
            return { outcome: 'success' };
          } catch (err) {
            console.error('[wagepro-web] leave: adjust balance failed:', err);
            return { outcome: 'failure' };
          }
        }
      : undefined;

  const opening_balance_action_visible =
    can_write && readyEntity !== null && state.status === 'ready';

  async function handle_recalculate(): Promise<void> {
    if (apply_entitlements === undefined) return;
    set_is_recalculating(true);
    set_recalc_message(null);
    const result = await apply_entitlements();
    set_is_recalculating(false);
    if (result.outcome === 'success') {
      set_recalc_message({
        tone: 'ok',
        text: t('leave.recalculate_done', {
          count: result.balances_changed,
        }),
      });
      return;
    }
    set_recalc_message({
      tone: 'error',
      text:
        result.failure === 'forbidden'
          ? t('leave.recalculate_forbidden')
          : t('leave.recalculate_error'),
    });
  }

  const opening_balance_button = opening_balance_action_visible ? (
    <Button
      variant="primary"
      size="sm"
      data-testid="leave-record-opening-balance"
      onClick={() => set_opening_picker_open(true)}
    >
      {t('leave.record_opening_balance_action')}
    </Button>
  ) : null;

  const recalculate_button =
    opening_balance_action_visible && apply_entitlements !== undefined ? (
      <Button
        variant="secondary"
        size="sm"
        data-testid="leave-recalculate-entitlements"
        loading={is_recalculating}
        onClick={() => {
          void handle_recalculate();
        }}
      >
        {t('leave.recalculate_action')}
      </Button>
    ) : null;

  const recalc_status =
    recalc_message !== null ? (
      <p
        role="status"
        data-testid="leave-recalculate-status"
        className={
          recalc_message.tone === 'error' ? 'nv-help is-error' : 'nv-help'
        }
      >
        {recalc_message.text}
      </p>
    ) : null;

  return (
    <>
      {is_loading && (
        <GlassTable>
          <div className="p-4">
            <span className="sr-only">{t('leave.balances_loading')}</span>
            <SkeletonList rows={5} />
          </div>
        </GlassTable>
      )}

      {state.status === 'no_entity' && (
        <EmptyState
          title={t('leave.no_entity_title')}
          description={t('leave.no_entity_description')}
        />
      )}

      {state.status === 'entitlement_required' && (
        <EmptyState
          title={t('leave.entitlement_required_title')}
          description={t('leave.entitlement_required_description')}
        />
      )}

      {state.status === 'error' && (
        <EmptyState
          title={t('leave.balances_error_title')}
          description={t('leave.balances_error_description')}
          action={
            <Button variant="secondary" onClick={retry}>
              {t('leave.retry')}
            </Button>
          }
        />
      )}

      {state.status === 'ready' && balances.length === 0 && (
        <EmptyState
          title={t('leave.balances_empty_title')}
          description={t('leave.balances_empty_description')}
          action={opening_balance_button}
        />
      )}

      {state.status === 'ready' && balances.length > 0 && (
        <>
          {(opening_balance_button !== null || recalculate_button !== null) && (
            <div className="flex flex-wrap items-center justify-end gap-2 mb-3">
              {recalc_status}
              {recalculate_button}
              {opening_balance_button}
            </div>
          )}
          <div className="overflow-x-auto">
            <GlassTable>
              <table className="nv-table">
                <thead>
                  <tr>
                    <th scope="col">{t('leave.balances_column_employee')}</th>
                    <th scope="col">{t('leave.balances_column_type')}</th>
                    <th scope="col" className="text-end">
                      {t('leave.balances_column_total')}
                    </th>
                    <th scope="col" className="text-end">
                      {t('leave.balances_column_taken')}
                    </th>
                    <th scope="col" className="text-end">
                      {t('leave.balances_column_booked')}
                    </th>
                    <th scope="col" className="text-end">
                      {t('leave.balances_column_pending')}
                    </th>
                    <th scope="col" className="text-end">
                      {t('leave.balances_column_available')}
                    </th>
                    {can_write && (
                      <th scope="col">
                        <span className="sr-only">{t('common.actions')}</span>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {balances.map((row) => {
                    const employee_name =
                      employeeNameById(employees, row.employee_id) ??
                      row.employee_id;
                    const leave_type_label = label_for_leave_type(
                      row.leave_type,
                    );
                    return (
                      <tr key={row.id}>
                        <td>{employee_name}</td>
                        <td>{leave_type_label}</td>
                        <td className="text-end num">
                          {format_days(row.total_days)}
                        </td>
                        <td className="text-end num">
                          {format_days(row.taken_days)}
                        </td>
                        <td className="text-end num">
                          {format_days(row.booked_days)}
                        </td>
                        <td className="text-end num">
                          {format_days(row.pending_days)}
                        </td>
                        <td className="text-end num">
                          {format_days(row.available_days)}
                        </td>
                        {can_write && (
                          <td>
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() =>
                                set_adjust_target({
                                  ...row,
                                  employee_name,
                                  leave_type_label,
                                })
                              }
                            >
                              {t('leave.balances_adjust_action')}
                            </Button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </GlassTable>
          </div>
        </>
      )}

      {adjust_fn !== undefined && (
        <LeaveAdjustDrawer
          target={adjust_target}
          onOpenChange={(open) => {
            if (!open) set_adjust_target(null);
          }}
          adjust={adjust_fn}
        />
      )}

      {opening_balance_action_visible && opening_picker_open && (
        <LeaveOpeningBalancePicker
          open={opening_picker_open}
          onOpenChange={set_opening_picker_open}
          employees={employees}
          employees_status={employees_status}
          onContinue={(picked) => {
            set_opening_picker_open(false);
            set_adjust_target(
              buildOpeningBalanceTarget({
                employee_id: picked.employee_id,
                entity_id: readyEntity.entity_id,
                leave_type: picked.leave_type,
                employee_name: picked.employee_name,
                leave_type_label: picked.leave_type_label,
              }),
            );
          }}
        />
      )}
    </>
  );
}
